import axios from 'axios';
import { apiError } from '../../components/features/errorBoundary/ErrorBoundary';
import i18n from '../../translations';
import { initInterceptorsApm } from './apm';

export default class BaseApi {
  constructor({ baseURL = process.env.API_URL ?? '', urlPrefix = '/api/v1' }) {
    this.prefix = `${baseURL}${urlPrefix}`;
    this.globalHeaders = {};
    this.apiClient = axios.create();

    initInterceptorsApm(this.apiClient);
  }

  _isAbsolute(str) {
    return /^(\w+:)?\/\//.test(str);
  }

  _hasPrefix(method, prefix) {
    return prefix && method.indexOf(prefix) === 0;
  }

  _getURL(method) {
    if (this._isAbsolute(method) || this._hasPrefix(method, this.prefix)) {
      return method;
    }

    return `${this.prefix}${method}`;
  }

  _initMethod(method) {
    return (apiMethod, data = {}) => this._send({ method, apiMethod, data });
  }

  _send({ apiMethod, timeout = 60000, ...params }) {
    const requestConfig = {
      ...params,
      headers: {
        ...this.globalHeaders,
        ...params?.headers,
        lang: i18n.language,
      },
      url: this._getURL(apiMethod),
    };

    const controller = new AbortController();
    requestConfig.signal = controller.signal;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    return this.apiClient(requestConfig)
      .then(this._onSuccess, this._onFail)
      .finally(() => {
        clearTimeout(timeoutId);
      });
  }

  _onSuccess(response) {
    const { data } = response;
    if ('success' in data) {
      if (!data.success) {
        const error = apiError(response);
        throw { error: error };
      }
      return data.data;
    }
    return data;
  }

  _onFail(error) {
    const errorData = apiError(error);
    throw { error: errorData };
  }

  addHeaders(headers) {
    Object.keys(headers).forEach((key) => {
      if (headers[key] === undefined) {
        delete this.globalHeaders[key];
      } else {
        this.globalHeaders[key] = headers[key];
      }
    });
  }

  // HTTP методы
  get(apiMethod, data = {}, options = {}) {
    return this._send({ method: 'get', apiMethod, data, ...options });
  }

  post(apiMethod, data = {}, options = {}) {
    return this._send({ method: 'post', apiMethod, data, ...options });
  }
}
